import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Calendar, Check, X, CheckSquare, Square } from 'lucide-react';
import { Card, CardContent } from "../../components/ui/card";
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import BackButton from '../../components/ui/BackNavigationButton';
import NavigationButton from '../../components/ui/NavigationButton';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/ui/ToolTip';
import Select from '../../components/forms/scheduleForm/Select';
import SelectItem from '../../components/forms/scheduleForm/SelectItem';
import SelectTrigger from '../../components/forms/scheduleForm/SelectTrigger';
import SelectContent from '../../components/forms/scheduleForm/SelectContent';
import SelectValue from '../../components/forms/scheduleForm/SelectValue';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';
import useAutoSave from '../../hooks/useAutoSave';

const AbsencesPage = () => {
  const [employeeAbsences, setEmployeeAbsences] = useState({});
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [totalDays, setTotalDays] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentAbsenceType, setCurrentAbsenceType] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartCell, setDragStartCell] = useState(null);
  const [lastHoveredCell, setLastHoveredCell] = useState(null);

  const absenceContainerRef = useRef(null);
  const absenceTypeRef = useRef(null);
  const nextButtonRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const isExampleMode = location.state?.isExample;

  useAutoSave('absences', employeeAbsences, 500, (absences) => {
    if (!isExampleMode && Object.keys(absences).length > 0) {
      const absenceEntries = [];
      
      Object.entries(absences).forEach(([employeeIndex, dayTypes]) => {
        Object.entries(dayTypes).forEach(([day, absenceType]) => {
          const scheduleIndex = findScheduleIndex(absenceType);
          if (scheduleIndex !== -1) {
            absenceEntries.push([parseInt(employeeIndex), scheduleIndex, parseInt(day)]);
          }
        });
      });
      
      sessionStorage.setItem('absenceEntries', JSON.stringify(absenceEntries));
      const savedAbsences = JSON.parse(sessionStorage.getItem('absences') || '{}');
      console.log('Absences saved:', savedAbsences);

      const savedAbsenceEntries = JSON.parse(sessionStorage.getItem('absenceEntries') || '[]');
      console.log('Absence entries saved:', savedAbsenceEntries);
    }
    
    return absences;
  })

  const tutorialSteps = [
    {
      title: t("tutorial.absence.title") || "Employee Absences",
      description: t("tutorial.absence.description") || "Here you can mark which days each employee will be absent and specify the type of absence.",
      position: "center"
    },
    {
      title: t("tutorial.absence.type.title") || "Absence Type Selection",
      description: t("tutorial.absence.type.description") || "Select an absence type first, then mark the days on the calendar.",
      position: "top-right",
      elementRef: absenceTypeRef
    },
    {
      title: t("tutorial.absence.selection.title") || "Absence Selection",
      description: t("tutorial.absence.selection.description") || "Click on days to mark them with the selected absence type.",
      position: "top-right",
      elementRef: absenceContainerRef
    },
    {
      title: t("tutorial.absence.next.title") || "Ready to Continue",
      description: t("tutorial.absence.next.description") || "Click Next to continue to the next step.",
      position: "next-button",
      elementRef: nextButtonRef
    }
  ];

  useEffect(() => {
    const loadData = () => {
      try {
        const savedEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
        setEmployees(savedEmployees);

        const savedSchedules = JSON.parse(sessionStorage.getItem('schedules') || '[]');
        setSchedules(savedSchedules);
        
        const absenceSchedules = savedSchedules.filter(
          schedule => ['FF', 'FE', 'FR', 'Abs', 'Cmp', 'HF', 'XX', 'YY'].includes(schedule.code)
        );
        
        setAbsenceTypes(absenceSchedules);
        if (absenceSchedules.length > 0) {
          setCurrentAbsenceType(absenceSchedules[0].code);
        }

        const generalInfo = JSON.parse(sessionStorage.getItem('general') || '{}');
        let days = 0;
        
        if (generalInfo.week_or_days === 0 && generalInfo.weeks) {
          days = generalInfo.weeks * 7;
        } else if (generalInfo.week_or_days === 1 && generalInfo.days) {
          days = generalInfo.days;
        }
        
        setTotalDays(days);

        if (isExampleMode) {
          const exampleAbsenceData = {
            "0": {
              "2": "FE",
              "3": "FE",
              "17": "FF"
            }
          };
          
          setEmployeeAbsences(exampleAbsenceData);
          sessionStorage.setItem('absences', JSON.stringify(exampleAbsenceData));
        } else {
          const savedAbsences = JSON.parse(sessionStorage.getItem('absences') || '{}');
          setEmployeeAbsences(savedAbsences);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        showAlert({
          title: t('alert.error'),
          description: t('alert.loadDataError'),
          variant: 'destructive'
        });
        setIsLoading(false);
      }
    };

    if (isExampleMode) {
      setShowTutorial(true);
    }
    loadData();
  }, [isExampleMode, showAlert, t]);

  const handleDayClick = (employeeIndex, day) => {
    if (isExampleMode || !currentAbsenceType) return;

    setEmployeeAbsences(prev => {
      const newAbsences = { ...prev };
      
      if (!newAbsences[employeeIndex]) {
        newAbsences[employeeIndex] = {};
      }
      
      if (newAbsences[employeeIndex][day] === currentAbsenceType) {
        const { [day]: _, ...restDays } = newAbsences[employeeIndex];
        newAbsences[employeeIndex] = restDays;
      } else {
        newAbsences[employeeIndex] = {
          ...newAbsences[employeeIndex],
          [day]: currentAbsenceType
        };
      }
      
      return newAbsences;
    });
  };

  const handleDayMouseDown = (employeeIndex, day) => {
    if (isExampleMode || !currentAbsenceType) return;
    
    setIsDragging(true);
    setDragStartCell({ employeeIndex, day });
    setLastHoveredCell({ employeeIndex, day });
    
    handleDayClick(employeeIndex, day);
  };

  const handleDayMouseEnter = (employeeIndex, day) => {
    if (!isDragging || isExampleMode || !currentAbsenceType) return;
    
    const currentCell = { employeeIndex, day };
    
    if (
      lastHoveredCell && 
      lastHoveredCell.employeeIndex === employeeIndex && 
      lastHoveredCell.day === day
    ) {
      return;
    }
    
    setLastHoveredCell(currentCell);
    
    if (dragStartCell.employeeIndex === employeeIndex) {
      handleDayClick(employeeIndex, day);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartCell(null);
    setLastHoveredCell(null);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const findScheduleIndex = (code) => {
    return schedules.findIndex(s => s.code === code);
  };

  const saveAbsences = () => {
    try {
      if (!isExampleMode) {
        const absenceEntries = [];
        
        Object.entries(employeeAbsences).forEach(([employeeIndex, dayTypes]) => {
          Object.entries(dayTypes).forEach(([day, absenceType]) => {
            const scheduleIndex = findScheduleIndex(absenceType);
            if (scheduleIndex !== -1) {
              absenceEntries.push([parseInt(employeeIndex), scheduleIndex, parseInt(day)]);
            }
          });
        });
        
        sessionStorage.setItem('absenceEntries', JSON.stringify(absenceEntries));
        sessionStorage.setItem('absences', JSON.stringify(employeeAbsences));

      }
      
      return true;
    } catch (error) {
      console.error('Error saving absences:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.saveError'),
        variant: 'destructive'
      });
      return false;
    }
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const handleBack = () => {
    navigate('/form/legislation', {
      state: { isExample: isExampleMode }
    });
  };

  const handleNext = () => {
    if (saveAbsences()) {
      const savedAbsences = JSON.parse(sessionStorage.getItem('absences') || '{}');
        console.log('Absences saved:', savedAbsences);

        const savedAbsenceEntries = JSON.parse(sessionStorage.getItem('absenceEntries') || '[]');
        console.log('Absence entries saved:', savedAbsenceEntries);
      navigate('/form/availability', {
        state: { isExample: isExampleMode }
      });
    }
  };

  const handleSelectAll = (employeeIndex) => {
    if (isExampleMode || !currentAbsenceType) return;
    
    setEmployeeAbsences(prev => {
      const newAbsences = { ...prev };
      
      if (!newAbsences[employeeIndex]) {
        newAbsences[employeeIndex] = {};
      }
      
      for (let day = 0; day < totalDays; day++) {
        newAbsences[employeeIndex][day] = currentAbsenceType;
      }
      
      return newAbsences;
    });
  };

  const handleDeselectAll = (employeeIndex) => {
    if (isExampleMode) return;
    
    setEmployeeAbsences(prev => {
      const newAbsences = { ...prev };
      
      if (!newAbsences[employeeIndex]) {
        return newAbsences;
      }
      
      const { [employeeIndex]: _, ...rest } = newAbsences;
      return rest;
    });
  };

  const getAbsenceTypeColor = (code) => {
    switch (code) {
      case 'FF': return 'bg-blue-500 hover:bg-blue-600'; 
      case 'FR': return 'bg-red-500 hover:bg-red-600';   
      case 'FE': return 'bg-green-500 hover:bg-green-600'; 
      case 'Abs': return 'bg-yellow-500 hover:bg-yellow-600'; 
      case 'Cmp': return 'bg-purple-500 hover:bg-purple-600'; 
      case 'HF': return 'bg-orange-500 hover:bg-orange-600'; 
      case 'XX': return 'bg-pink-500 hover:bg-pink-600';  
      case 'YY': return 'bg-indigo-500 hover:bg-indigo-600'; 
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const getAbsenceTypeDescription = (code) => {
    const schedule = schedules.find(s => s.code === code);
    return schedule ? `${schedule.code} (${schedule.legend || schedule.description})` : code;
  };

  const renderDayGrid = (employeeIndex) => {
    if (totalDays <= 0) return null;
    
    const days = Array.from({ length: totalDays }, (_, i) => i);
    const rowSize = 7; 
    const rows = [];
    
    for (let i = 0; i < days.length; i += rowSize) {
      rows.push(days.slice(i, i + rowSize));
    }
    
    return (
      <div className="mt-2">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {[t("form.absence.mon"), t("form.absence.tue"), t("form.absence.wed"), t("form.absence.thu"), t("form.absence.fri"), t("form.absence.sat"), t("form.absence.sun")].map((day, i) => (
            <div key={i} className="text-xs text-center text-gray-600">
              {day}
            </div>
          ))}
        </div>
        
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-7 gap-1 mb-1">
            {row.map(day => {
              const absenceType = employeeAbsences[employeeIndex]?.[day];
              const isSelected = absenceType !== undefined;
              const isCurrentType = absenceType === currentAbsenceType;
              
              return (
                <button
                  key={day}
                  onMouseDown={() => handleDayMouseDown(employeeIndex, day)}
                  onMouseEnter={() => handleDayMouseEnter(employeeIndex, day)}
                  disabled={isExampleMode}
                  className={`
                    h-8 w-full text-xs rounded flex items-center justify-center transition-colors
                    ${isSelected 
                      ? getAbsenceTypeColor(absenceType) + ' text-white' 
                      : 'bg-gray-100 hover:bg-gray-200'}
                    ${isExampleMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                  `}
                  title={isSelected ? getAbsenceTypeDescription(absenceType) : ''}
                >
                  {day + 1}
                  {isSelected && (
                    <span className="ml-1">
                      {isCurrentType ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="-mt-16">
      <Breadcrumbs pages={pages} />
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-6xl mx-auto space-y-8">
          <ScenarioIndicator />
          <div className="flex justify-between items-center">
            <BackButton onClick={handleBack} />
            <div className="flex items-center gap-2 mb-6">
              <h1 className="text-2xl font-bold">
                {t("form.absence.title") || "Employee Absences"}
              </h1>
              <div className="flex items-center h-8">
                <Tooltip
                  content={t("form.absence.tooltip") || "Set which days each employee will be absent and specify the type of absence."}
                />
              </div>
            </div>
            <NavigationButton ref={nextButtonRef} onClick={handleNext} />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : employees.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-amber-500 mb-4">
                  <Calendar className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium">
                  {t("form.absence.noEmployees") || "No employees found"}
                </h3>
                <p className="text-gray-500 mt-2 mb-4">
                  {t("form.absence.selectTeamFirst") || "Please select a team first"}
                </p>
                <button
                  onClick={handleBack}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {t("form.button.back") || "Go Back"}
                </button>
              </CardContent>
            </Card>
          ) : absenceTypes.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-amber-500 mb-4">
                  <Calendar className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium">
                  {t("form.absence.noAbsenceTypes") || "No absence types found"}
                </h3>
                <p className="text-gray-500 mt-2 mb-4">
                  {t("form.absence.defineSchedulesFirst") || "Please define absence schedules first (FF, FR, Abs, etc.)"}
                </p>
                <button
                  onClick={() => navigate('/form/schedule', { state: { isExample: isExampleMode } })}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {t("form.button.defineSchedules") || "Define Schedules"}
                </button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="mb-6" ref={absenceTypeRef}>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                      <label className="text-sm font-medium text-gray-700 min-w-32">
                        {t("form.absence.selectType") || "Select Absence Type:"}
                      </label>
                      <div className="w-full md:w-72">
                        <Select
                          value={currentAbsenceType}
                          onValueChange={setCurrentAbsenceType}
                          disabled={isExampleMode}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={t("form.absence.choosetype") || "Choose an absence type"} />
                          </SelectTrigger>
                          <SelectContent>
                            {absenceTypes.map((type) => (
                              <SelectItem key={type.code} value={type.code}>
                                {getAbsenceTypeDescription(type.code)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                        {absenceTypes.map((type) => (
                          <div 
                            key={type.code} 
                            className="flex items-center gap-1"
                            title={type.description || type.legend}
                          >
                            <div 
                              className={`w-4 h-4 rounded-full ${getAbsenceTypeColor(type.code)}`}
                            ></div>
                            <span className="text-xs text-gray-600">{type.code}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div ref={absenceContainerRef}>
                {employees.map((employee, index) => {
                  const employeeAbsenceCount = employeeAbsences[index] 
                    ? Object.keys(employeeAbsences[index]).length 
                    : 0;
                    
                  return (
                    <Card key={index} className="mb-4">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center mb-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="font-semibold text-indigo-600">
                                {employee.name.substring(0, 2)}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{employee.name}</h3>
                              <p className="text-sm text-gray-500">
                                {employeeAbsenceCount} {t("form.absence.days") || "absence days"}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-end flex-wrap gap-2 mt-4 mb-3">
                            <button
                              onClick={() => handleSelectAll(index)}
                              disabled={isExampleMode || !currentAbsenceType}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs 
                                ${isExampleMode || !currentAbsenceType 
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}
                              `}
                              title={t("form.absence.selectAll") || "Select all days"}
                            >
                              <CheckSquare className="h-3 w-3" />
                              {t("form.absence.selectAll") || "Select All"}
                            </button>
                            <button
                              onClick={() => handleDeselectAll(index)}
                              disabled={isExampleMode || !employeeAbsenceCount}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs 
                                ${isExampleMode || !employeeAbsenceCount
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'}
                              `}
                              title={t("form.absence.deselectAll") || "Clear all absences"}
                            >
                              <Square className="h-3 w-3" />
                              {t("form.absence.deselectAll") || "Clear All"}
                            </button>
                          </div>
                        </div>
                        
                        {renderDayGrid(index)}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      
      <TutorialOverlay 
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />
    </div>
  );
};

export default AbsencesPage;